import { type ComponentProps, type ReactNode } from 'react';
import { cn } from '@shared/lib/cn';

/**
 * Card elevation tiers — picks the visual weight of the surface against the
 * page background. Use:
 *   - `flat`     (default) — borderless lookalike: a thin border + no drop
 *                shadow. Right for list items, dense rows, anywhere multiple
 *                Cards stack vertically.
 *   - `raised`   — `shadow-2` (a soft drop with a hairline edge). Right for a
 *                single highlighted Card on a page (active workout block,
 *                form container) that should pop without screaming.
 *   - `featured` — `shadow-brand` (brand-tinted glow + brand-edged outline).
 *                Reserved for the page's hero CTA, PR cards, milestone
 *                callouts. There should be at most one `featured` Card per
 *                screen — otherwise the emphasis erodes.
 */
const ELEVATION = {
  flat: 'shadow-none',
  raised: 'shadow-2',
  featured: 'shadow-brand border-brand/30',
} as const;
export type CardElevation = keyof typeof ELEVATION;

export function Card({
  className,
  interactive,
  elevation = 'flat',
  ...props
}: ComponentProps<'div'> & {
  interactive?: boolean;
  elevation?: CardElevation;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        'border-border bg-card text-card-foreground rounded-lg border',
        ELEVATION[elevation],
        interactive &&
          'hover:bg-muted/30 focus-visible:ring-ring cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Surface — Card-shaped container with a subtle tinted background.
 * Use for grouped meta (previous-set reference, just-finished read-back,
 * coach-context callouts) where a full Card reads heavier than the content
 * deserves. Tone variants resolve to muted / cyan / amber / green / red.
 */
const SURFACE_TONES = {
  default: 'border-border bg-muted/40',
  info: 'border-cyan-500/30 bg-cyan-500/10',
  warning: 'border-amber-500/30 bg-amber-500/10',
  success: 'border-green-500/30 bg-green-500/10',
  danger: 'border-destructive/30 bg-destructive/10',
  brand: 'border-brand/30 bg-brand/10',
} as const;
export type SurfaceTone = keyof typeof SURFACE_TONES;

export function Surface({
  tone = 'default',
  className,
  ...props
}: ComponentProps<'div'> & { tone?: SurfaceTone }): React.JSX.Element {
  return (
    <div
      className={cn('rounded-md border px-3 py-2 text-sm', SURFACE_TONES[tone], className)}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: ComponentProps<'div'>): React.JSX.Element {
  return <div className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />;
}

export function CardTitle({
  className,
  children,
  ...props
}: ComponentProps<'h3'> & { children: ReactNode }): React.JSX.Element {
  return (
    <h3 className={cn('text-h2 leading-tight font-semibold tracking-tight', className)} {...props}>
      {children}
    </h3>
  );
}

export function CardDescription({ className, ...props }: ComponentProps<'p'>): React.JSX.Element {
  return <p className={cn('text-muted-foreground text-sm', className)} {...props} />;
}

export function CardContent({ className, ...props }: ComponentProps<'div'>): React.JSX.Element {
  return <div className={cn('p-6 pt-0', className)} {...props} />;
}

export function CardFooter({ className, ...props }: ComponentProps<'div'>): React.JSX.Element {
  return <div className={cn('flex items-center p-6 pt-0', className)} {...props} />;
}
